// src/pages/AppointmentRequestPage.tsx
import { Button } from "@chakra-ui/react";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Request } from "../apis/requests";
import customToast, { ToastType } from "../components/Toast";

type Mode = "Online" | "Offline";
type Who = "new" | "registered";

const getTodayLocalDate = () => {
  const now = new Date();
  const timezoneOffset = now.getTimezoneOffset() * 60 * 1000;

  return new Date(now.getTime() - timezoneOffset).toISOString().split("T")[0];
};

const inputClassName =
  "mt-1.5 min-h-12 w-full rounded-xl border border-gray-300 bg-white px-3.5 py-3 text-base text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-100";

const labelClassName = "block text-sm font-semibold leading-5 text-gray-800";

const helperTextClassName = "mt-1.5 text-xs leading-5 text-gray-500";

const AppointmentRequestPage: React.FC = () => {
  const navigate = useNavigate();

  const [who, setWho] = useState<Who>("new");
  const [loading, setLoading] = useState(false);

  const today = getTodayLocalDate();

  // New patient form
  const [newForm, setNewForm] = useState({
    name: "",
    email: "",
    phone: "",
    doctorName: "",
    mode: "Online" as Mode,
    preferredDate: "",
    preferredTime: "",
    notes: "",
  });

  // Registered patient form
  const [regForm, setRegForm] = useState({
    patientId: "",
    name: "",
    email: "",
    phone: "",
    doctorName: "",
    mode: "Online" as Mode,
    preferredDate: "",
    preferredTime: "",
    notes: "",
  });

  const onChangeNew = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setNewForm((current) => ({
      ...current,
      [e.target.name]: e.target.value,
    }));
  };

  const onChangeReg = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setRegForm((current) => ({
      ...current,
      [e.target.name]: e.target.value,
    }));
  };

  const isValidNew = Boolean(newForm.name.trim() && newForm.email.trim());

  const isValidReg = Boolean(regForm.patientId.trim() && regForm.email.trim());

  const canSubmit = (who === "new" ? isValidNew : isValidReg) && !loading;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      if (who === "new") {
        await Request.post("/public/appointment-requests", {
          name: newForm.name,
          email: newForm.email,
          phone: newForm.phone || undefined,
          doctorName: newForm.doctorName || undefined,
          mode: newForm.mode,
          preferredDate: newForm.preferredDate || undefined,
          preferredTime: newForm.preferredTime || undefined,
          notes: newForm.notes || undefined,
        });

        customToast(
          "Request sent. We'll contact you shortly.",
          ToastType.success,
        );

        setNewForm({
          name: "",
          email: "",
          phone: "",
          doctorName: "",
          mode: "Online",
          preferredDate: "",
          preferredTime: "",
          notes: "",
        });
      } else {
        await Request.post("/public/appointment-requests", {
          name: regForm.name || "Registered patient",
          email: regForm.email,
          phone: regForm.phone || undefined,
          doctorName: regForm.doctorName || undefined,
          mode: regForm.mode,
          preferredDate: regForm.preferredDate || undefined,
          preferredTime: regForm.preferredTime || undefined,
          notes: regForm.notes || undefined,
          patientId: regForm.patientId,
        });

        customToast(
          "Request sent. We'll contact you shortly.",
          ToastType.success,
        );

        setRegForm({
          patientId: "",
          name: "",
          email: "",
          phone: "",
          doctorName: "",
          mode: "Online",
          preferredDate: "",
          preferredTime: "",
          notes: "",
        });
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send request.";

      customToast(Array.isArray(msg) ? msg.join(", ") : msg, ToastType.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-5 sm:px-6 sm:py-8">
      <div className="mx-auto w-full max-w-2xl">
        {/* Header */}
        <header className="mb-5">
          <p className="mb-1 text-sm font-semibold text-blue-600">
            Ubimed Consultation
          </p>

          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            Request an appointment
          </h1>

          <p className="mt-2 max-w-xl text-sm leading-5 text-gray-600">
            Choose how you'd prefer to consult. We'll review your request and
            contact you to confirm the appointment.
          </p>
        </header>

        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          {/* Patient type */}
          <fieldset className="mb-5">
            <legend className="mb-2 text-sm font-semibold text-gray-800">
              Patient type
            </legend>

            <div className="grid grid-cols-2 gap-2.5">
              {/* New patient */}
              <label className="relative cursor-pointer">
                <input
                  type="radio"
                  name="who"
                  value="new"
                  checked={who === "new"}
                  onChange={() => setWho("new")}
                  className="sr-only"
                />

                <span
                  className={`
                    flex min-h-12 items-center justify-center
                    rounded-xl border px-3 py-3 text-center
                    text-sm font-semibold transition
                    ${
                      who === "new"
                        ? "border-blue-600 bg-blue-50 text-blue-700 ring-1 ring-blue-100"
                        : "border-gray-300 bg-white text-gray-700"
                    }
                  `}
                >
                  {who === "new" && (
                    <span className="mr-1.5" aria-hidden="true">
                      ✓
                    </span>
                  )}
                  New patient
                </span>
              </label>

              {/* Registered patient */}
              <label className="relative cursor-pointer">
                <input
                  type="radio"
                  name="who"
                  value="registered"
                  checked={who === "registered"}
                  onChange={() => setWho("registered")}
                  className="sr-only"
                />

                <span
                  className={`
                    flex min-h-12 items-center justify-center
                    rounded-xl border px-3 py-3 text-center
                    text-sm font-semibold transition
                    ${
                      who === "registered"
                        ? "border-blue-600 bg-blue-50 text-blue-700 ring-1 ring-blue-100"
                        : "border-gray-300 bg-white text-gray-700"
                    }
                  `}
                >
                  {who === "registered" && (
                    <span className="mr-1.5" aria-hidden="true">
                      ✓
                    </span>
                  )}
                  Registered patient
                </span>
              </label>
            </div>
          </fieldset>

          <div className="mb-5 border-t border-gray-100" />

          <form
            id="appointment-request-form"
            onSubmit={submit}
            className="space-y-4"
            aria-busy={loading}
          >
            {who === "new" ? (
              <>
                {/* Full name */}
                <div>
                  <label htmlFor="new-name" className={labelClassName}>
                    Full name{" "}
                    <span className="text-red-600" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only"> required</span>
                  </label>

                  <input
                    id="new-name"
                    name="name"
                    type="text"
                    value={newForm.name}
                    onChange={onChangeNew}
                    className={inputClassName}
                    autoComplete="name"
                    autoCapitalize="words"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="new-email" className={labelClassName}>
                    Email address{" "}
                    <span className="text-red-600" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only"> required</span>
                  </label>

                  <input
                    id="new-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    value={newForm.email}
                    onChange={onChangeNew}
                    className={inputClassName}
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="you@example.com"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="new-phone" className={labelClassName}>
                    Phone number{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>

                  <input
                    id="new-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    value={newForm.phone}
                    onChange={onChangeNew}
                    className={inputClassName}
                    autoComplete="tel"
                    placeholder="Enter your phone number"
                  />
                </div>

                {/* Preferred doctor */}
                <div>
                  <label htmlFor="new-doctor" className={labelClassName}>
                    Preferred doctor{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>

                  <input
                    id="new-doctor"
                    name="doctorName"
                    type="text"
                    value={newForm.doctorName}
                    onChange={onChangeNew}
                    className={inputClassName}
                    autoCapitalize="words"
                    placeholder="Doctor's name, if you have a preference"
                  />
                </div>

                {/* Consultation type */}
                <div>
                  <label htmlFor="new-mode" className={labelClassName}>
                    Consultation type
                  </label>

                  <select
                    id="new-mode"
                    name="mode"
                    value={newForm.mode}
                    onChange={onChangeNew}
                    className={inputClassName}
                  >
                    <option value="Online">Online consultation</option>
                    <option value="Offline">In-person consultation</option>
                  </select>
                </div>

                {/* Date and time */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="new-preferred-date"
                      className={labelClassName}
                    >
                      Preferred date
                    </label>

                    <input
                      id="new-preferred-date"
                      name="preferredDate"
                      type="date"
                      min={today}
                      value={newForm.preferredDate}
                      onChange={onChangeNew}
                      className={inputClassName}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="new-preferred-time"
                      className={labelClassName}
                    >
                      Preferred time
                    </label>

                    <input
                      id="new-preferred-time"
                      name="preferredTime"
                      type="time"
                      value={newForm.preferredTime}
                      onChange={onChangeNew}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <p className={helperTextClassName}>
                  Preferred dates and times are subject to confirmation.
                </p>

                {/* Additional information */}
                <div>
                  <label htmlFor="new-notes" className={labelClassName}>
                    Additional information{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>

                  <textarea
                    id="new-notes"
                    name="notes"
                    value={newForm.notes}
                    onChange={onChangeNew}
                    rows={3}
                    className={`${inputClassName} resize-y`}
                    placeholder="Add any information that may help us arrange your consultation"
                  />
                </div>
              </>
            ) : (
              <>
                {/* Patient ID */}
                <div>
                  <label
                    htmlFor="registered-patient-id"
                    className={labelClassName}
                  >
                    Patient ID{" "}
                    <span className="text-red-600" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only"> required</span>
                  </label>

                  <input
                    id="registered-patient-id"
                    name="patientId"
                    type="text"
                    inputMode="numeric"
                    value={regForm.patientId}
                    onChange={onChangeReg}
                    className={inputClassName}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="Enter your Ubimed patient ID"
                    required
                  />

                  <p className={helperTextClassName}>
                    Use the patient ID linked to your Ubimed record.
                  </p>
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="registered-name" className={labelClassName}>
                    Full name{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>

                  <input
                    id="registered-name"
                    name="name"
                    type="text"
                    value={regForm.name}
                    onChange={onChangeReg}
                    className={inputClassName}
                    autoComplete="name"
                    autoCapitalize="words"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="registered-email" className={labelClassName}>
                    Email address{" "}
                    <span className="text-red-600" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only"> required</span>
                  </label>

                  <input
                    id="registered-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    value={regForm.email}
                    onChange={onChangeReg}
                    className={inputClassName}
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="you@example.com"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="registered-phone" className={labelClassName}>
                    Phone number{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>

                  <input
                    id="registered-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    value={regForm.phone}
                    onChange={onChangeReg}
                    className={inputClassName}
                    autoComplete="tel"
                    placeholder="Enter your phone number"
                  />
                </div>

                {/* Preferred doctor */}
                <div>
                  <label htmlFor="registered-doctor" className={labelClassName}>
                    Preferred doctor{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>

                  <input
                    id="registered-doctor"
                    name="doctorName"
                    type="text"
                    value={regForm.doctorName}
                    onChange={onChangeReg}
                    className={inputClassName}
                    autoCapitalize="words"
                    placeholder="Doctor's name, if you have a preference"
                  />
                </div>

                {/* Consultation type */}
                <div>
                  <label htmlFor="registered-mode" className={labelClassName}>
                    Consultation type
                  </label>

                  <select
                    id="registered-mode"
                    name="mode"
                    value={regForm.mode}
                    onChange={onChangeReg}
                    className={inputClassName}
                  >
                    <option value="Online">Online consultation</option>
                    <option value="Offline">In-person consultation</option>
                  </select>
                </div>

                {/* Date and time */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="registered-preferred-date"
                      className={labelClassName}
                    >
                      Preferred date
                    </label>

                    <input
                      id="registered-preferred-date"
                      name="preferredDate"
                      type="date"
                      min={today}
                      value={regForm.preferredDate}
                      onChange={onChangeReg}
                      className={inputClassName}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="registered-preferred-time"
                      className={labelClassName}
                    >
                      Preferred time
                    </label>

                    <input
                      id="registered-preferred-time"
                      name="preferredTime"
                      type="time"
                      value={regForm.preferredTime}
                      onChange={onChangeReg}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <p className={helperTextClassName}>
                  Preferred dates and times are subject to confirmation.
                </p>

                {/* Additional information */}
                <div>
                  <label htmlFor="registered-notes" className={labelClassName}>
                    Additional information{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </label>

                  <textarea
                    id="registered-notes"
                    name="notes"
                    value={regForm.notes}
                    onChange={onChangeReg}
                    rows={3}
                    className={`${inputClassName} resize-y`}
                    placeholder="Add any information that may help us arrange your consultation"
                  />
                </div>
              </>
            )}

            {/* Loading announcement */}
            <p className="sr-only" role="status" aria-live="polite">
              {loading ? "Sending your appointment request." : ""}
            </p>

            {/* Submit */}
            <div className="pt-1">
              <Button
                type="submit"
                disabled={!canSubmit}
                width="100%"
                minHeight="48px"
                borderRadius="12px"
                backgroundColor={canSubmit ? "#2563eb" : "#f3f4f6"}
                color={canSubmit ? "#ffffff" : "#6b7280"}
                fontSize="16px"
                fontWeight="600"
                opacity={1}
                cursor={canSubmit ? "pointer" : "not-allowed"}
                _hover={
                  canSubmit
                    ? {
                        backgroundColor: "#1d4ed8",
                      }
                    : {
                        backgroundColor: "#f3f4f6",
                      }
                }
                _disabled={{
                  opacity: 1,
                  backgroundColor: "#f3f4f6",
                  color: "#6b7280",
                  cursor: "not-allowed",
                }}
              >
                {loading ? "Sending request..." : "Submit appointment request"}
              </Button>

              <p className="mt-2.5 text-center text-xs leading-5 text-gray-500">
                Submitting this form does not confirm an appointment. We'll
                contact you after reviewing your request.
              </p>
            </div>

            {/* Navigation */}
            <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={loading}
                className="
                  flex min-h-12 w-full items-center justify-center
                  rounded-xl border border-gray-300 bg-gray-50
                  px-4 py-3 text-sm font-semibold text-gray-800
                  transition hover:bg-gray-100
                  focus:outline-none focus:ring-2
                  focus:ring-blue-500 focus:ring-offset-2
                  disabled:cursor-not-allowed disabled:opacity-50
                "
              >
                Back
              </button>

              <Link
                to="/"
                aria-disabled={loading}
                onClick={(event) => {
                  if (loading) {
                    event.preventDefault();
                  }
                }}
                className={`
                  flex min-h-12 w-full items-center justify-center
                  rounded-xl border border-gray-300 bg-white
                  px-4 py-3 text-center text-sm font-semibold
                  text-gray-800 transition
                  ${
                    loading
                      ? "pointer-events-none opacity-50"
                      : "hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  }
                `}
              >
                Home
              </Link>
            </div>
          </form>
        </section>

        {/* Safety note */}
        <p className="mx-auto mt-4 max-w-xl px-3 pb-5 text-center text-xs leading-5 text-gray-500">
          If you need urgent medical attention, use the appropriate emergency
          medical service in your location rather than waiting for an
          appointment request to be reviewed.
        </p>
      </div>
    </main>
  );
};

export default AppointmentRequestPage;
